import React, { useState, useRef, useEffect } from "react";
import "./DropDown.scss";
import AuiIcon from "../AuiDropdown/AuiIcon";
export type viewType = "vertical" | "horizontal";

export interface Props {
  mainClassName?: string;
  dropContentClass?: string;
  dropClassName?: string;
  children?: any;
  mainId?: string;
  dropId?: string;
  dropdownLabel?: string;
  data?: any;
  listButtonClick?: any;
  listInBtn?: boolean;
  placeHolder?: string;
  view?: viewType;
  required?: boolean;
  errorText?: string;
  onChange?: (selectedValue: string) => void;
  clearFilterData?: () => void;
  value?: any;
  setSelectedIndex?: any;
  clearError?: boolean; 
}
const DropDown = ({
  mainClassName,
  children,
  mainId,
  dropdownLabel,
  dropClassName,
  dropId = "dropdown-id",
  dropContentClass,
  data,
  listButtonClick,
  listInBtn = false,
  view = "vertical",
  placeHolder = "Select Value...",
  required,
  errorText = "",
  onChange,
  clearFilterData,
  value: initialValue,
  setSelectedIndex,
  clearError = false,
}: Props) => {
  const ref = useRef<any>();
  const butref = useRef<any>();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<string | undefined>(initialValue);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const validateField = (newValue?: string) => {
    if ((touched || !newValue) && (!newValue || newValue.trim() === "")) {
      setError(errorText);
    } else {
      setError("");
    }
  };

  const handleBlur = () => {
    setTouched(true);
    validateField(value);
  };

  useEffect(() => {
    if (clearError) {
      setError("");
    }
  }, [clearError]);

  const handleToggle = () => {
    setOpen(!open);
    if (!value && touched) {
      setError(errorText);
    }
  };
  const handleClickOutside = (event: any) => {
    if (ref.current && !ref.current.contains(event.target)) {
      setOpen(false);
    }
    if (!value && touched) {
      setError(errorText);
    }
  };
  const clickedItem = (selectedValue: string, index?: number) => {
    if (!selectedValue) {
      setError(errorText);
    } else {
      setError("");
    }
    setValue(selectedValue);
    setOpen(false);
    butref?.current?.focus();
    if (onChange) {
      onChange(selectedValue);
    }
    if (setSelectedIndex) {
      setSelectedIndex(index);
    }
  };
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        butref?.current?.focus();
      }
    });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);
  
    useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;

      // Close dropdown and move focus to next element on Tab
      if (e.key === "Tab") {
        setOpen(false);
        setFocusedIndex(null);
        return;
      }

      // Escape key to close dropdown
      if (e.key === "Escape") {
        setOpen(false);
        butref?.current?.focus();
        return;
      }

      if (!data?.length) return;

      // Arrow Down
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedIndex((prev) => {
          const nextIndex = prev === null ? 0 : Math.min(prev + 1, data.length - 1);
          itemRefs.current[nextIndex]?.focus();
          return nextIndex;
        });
      }

      // Arrow Up
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedIndex((prev) => {
          const nextIndex = prev === null ? data.length - 1 : Math.max(prev - 1, 0);
          itemRefs.current[nextIndex]?.focus();
          return nextIndex;
        });
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, data]);

  return (
    <div
      className={`dropdown-wrapper${
        mainClassName ? " " + mainClassName : ""
      } ${view}`}
      id={mainId}
      data-testid="org-filter-id"
      ref={ref}
    >
       {dropdownLabel && (
        <label id={`${dropId}-label`}>
          {dropdownLabel}
          {required && (
            <>
              <span className="visually-hidden">&nbsp;required</span>
              <span className="requiredLabel" aria-hidden="true">
                *
              </span>
            </>
          )}
        </label>
      )}
      <div className={`dropdownText${required ? " required" : ""}`}>
        <button
          className={`dropDown${
            dropClassName ? " " + dropClassName : ""
          } d-flex align-items-center justify-content-between`}
          onClick={handleToggle}
          aria-expanded={open ? "true" : "false"}
          aria-haspopup="listbox"
          aria-controls={`${dropId}-listbox`}
          aria-labelledby={`${dropId}-label`}
          aria-activedescendant={
            open && focusedIndex !== null
            ? `${data?.[focusedIndex]?.split(" ").join("").substring(0, 8)}-${focusedIndex}`
              : undefined
          }
          aria-describedby={error ? `${dropId}-error` : undefined}
          aria-invalid={!!error}
          ref={butref}
          tabIndex={0}
          onBlur={handleBlur}
          role="combobox"
          aria-required={required}
        >
          <span className="placeholdText">{value && value !== "" ? value : placeHolder}</span>
        </button>
        {value && (
          <button
            className="close-icon"
            onClick={(e) => {
              e.stopPropagation();
              clickedItem("");
              clearFilterData?.();
            }}
            data-testid="clear-search-id"
            aria-label={`Clear ${dropdownLabel} option`}
          >
            <AuiIcon iconClass="closeIcon" svgheight="12" svgwidth="12" />
          </button>
        )}
        {!value && error && <span id={`${dropId}-error`} className="dropError">{error}</span>}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleToggle();
          }}
          className="dropicon"
          tabIndex={-1}
        >
          <AuiIcon iconClass={open ? "arrowup" : "arrowdown"} svgheight="20" svgwidth="20" />
        </button>
        {(children || data) && open && (
          <div className={`dropContent${dropContentClass ? " " + dropContentClass : ""}`}>
            {children}
            {data && (
              <ul id={`${dropId}-listbox`} aria-labelledby={`${dropId}-label`}>
                {data?.map((dataValue: any, index: number) => {
                  const itemKey = `${dataValue?.split(" ").join("").substring(0, 5)}-${index}`;
                  const itemId = `${dataValue?.split(" ").join("").substring(0, 8)}-${index}`;
                  return (
                    <li
                      key={itemKey}
                      id={itemId}
                      role="none"
                    >
                      {listInBtn ? (
                        <button
                          className={value === dataValue ? "selected" : ""}
                          onClick={() => {
                            listButtonClick?.();
                            handleToggle();
                            clickedItem(dataValue, index);
                          }}
                          role="option"
                          title={dataValue}
                          ref={(el) => (itemRefs.current[index] = el)}
                          tabIndex={-1}
                          aria-selected={value === dataValue}
                        >
                          {dataValue}
                        </button>
                      ) : (
                        dataValue
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
export default DropDown;
